import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dumbbell, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';
import { PasswordInput } from '../components/PasswordInput';
import { MFAChallengeFlow } from '../components/MFAChallengeFlow';

export const LoginPage = () => {
  const { login, completeMfaLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  // Iter-46ah: when login returns mfa_required / mfa_enrollment_required we
  // park the bridge token here and swap the form for the MFA challenge UI.
  const [mfaChallenge, setMfaChallenge] = useState(null); // { mode, token }

  const from = location.state?.from?.pathname || '/dashboard';

  const finishLogin = (data) => {
    // Show a grace-warning toast if the backend told us how many free logins
    // remain. Surface this every time so the user can't miss it.
    if (data?.mfa_grace_remaining != null) {
      toast.warning(
        `You have ${data.mfa_grace_remaining} login${data.mfa_grace_remaining === 1 ? '' : 's'} remaining before two-factor authentication is required. Set it up in Profile.`,
        { duration: 7000 }
      );
    } else {
      toast.success('Welcome back!');
    }
    navigate(from, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(formData.email, formData.password);
      if (data?.mfa_required) {
        setMfaChallenge({ mode: 'verify', token: data.mfa_token });
        return;
      }
      if (data?.mfa_enrollment_required) {
        setMfaChallenge({ mode: 'enroll', token: data.mfa_token });
        toast.warning('Two-factor authentication is now required for your account.');
        return;
      }
      finishLogin(data);
    } catch (error) {
      toast.error(formatApiError(error, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaComplete = (data) => {
    completeMfaLogin(data);
    setMfaChallenge(null);
    finishLogin(data);
  };

  const handleMfaCancel = () => {
    setMfaChallenge(null);
    setFormData({ email: formData.email, password: '' });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Dumbbell className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-bold text-2xl">StudioScheduler</span>
          </Link>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-0">
            <CardTitle className="text-2xl">
              {mfaChallenge ? (mfaChallenge.mode === 'enroll' ? 'Set Up MFA' : 'Two-Factor Authentication') : 'Welcome Back'}
            </CardTitle>
            <CardDescription>
              {mfaChallenge
                ? (mfaChallenge.mode === 'enroll'
                    ? 'Your grace period has ended — set up MFA to continue'
                    : 'Enter the code from your authenticator app')
                : 'Sign in to your account to continue'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {mfaChallenge ? (
              <MFAChallengeFlow
                mode={mfaChallenge.mode}
                mfaToken={mfaChallenge.token}
                onComplete={handleMfaComplete}
                onCancel={handleMfaCancel}
              />
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 btn-lift shadow-lg shadow-primary/20"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
            )}

            {!mfaChallenge && (
            <div className="mt-6 text-center text-sm space-y-2">
              <div>
                <Link to="/forgot-password" className="text-muted-foreground hover:text-primary hover:underline" data-testid="login-forgot-link">
                  Forgot your password?
                </Link>
              </div>
              <div>
                <span className="text-muted-foreground">Don't have an account? </span>
                <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-register-link">
                  Sign up
                </Link>
              </div>
            </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'client'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(formatApiError(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Dumbbell className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-bold text-2xl">StudioScheduler</span>
          </Link>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-0">
            <CardTitle className="text-2xl">Create Account</CardTitle>
            <CardDescription>Start your fitness journey today</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    data-testid="register-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    placeholder="Doe"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    data-testid="register-lastname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  placeholder="At least 10 characters, 1 letter, 1 number"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={10}
                  data-testid="register-password-input"
                />
                <p className="text-xs text-muted-foreground" data-testid="register-password-hint">
                  Minimum 10 characters, must include at least one letter and one number.
                </p>
              </div>
              <div className="space-y-2">
                <Label>I want to</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'client' })}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      formData.role === 'client' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="register-role-client"
                  >
                    <p className="font-medium">Join as Client</p>
                    <p className="text-xs text-muted-foreground">Book classes & train</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'studio_owner' })}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      formData.role === 'studio_owner' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="register-role-owner"
                  >
                    <p className="font-medium">Create Studio</p>
                    <p className="text-xs text-muted-foreground">Manage your business</p>
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 btn-lift shadow-lg shadow-primary/20"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
